import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import Logo from "./Logo";
import { useLiveRate } from "./RateStrip";

const POPULAR_SEARCHES = ["gold coin 1 gm", "25 mg gold coin", "silver anklet", "mangalsutra", "jhumka", "name pendant", "silver coin", "vanki ring", "belpatra", "wedding coin"];

const POLICY_LINKS = [
  ["Track your order", "/track-order"],
  ["FAQs", "/faq"],
  ["Lifetime buyback", "/policies/buyback"],
  ["Gold & silver exchange", "/policies/exchange"],
  ["Verify a certificate", "/verify"],
  ["Gold rate by city", "/gold-rate"],
  ["Materials & quality", "/materials"],
  ["Corporate gifting", "/corporate-gifting"],
];

export default function Footer() {
  const { config } = useStore();
  const [cities, setCities] = useState([]);
  const rate = useLiveRate();
  const rateLocations = config?.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"];

  useEffect(() => {
    api.get("/rates/cities").then((r) => setCities((r.data || []).slice(0, 8))).catch(() => {});
  }, []);

  const legal = config?.legal || {};
  const partners = config?.channel_partners || [];

  return (
    <footer className="bg-brand-depth text-ivory/85 mt-20" data-testid="footer">
      {/* gold hairline */}
      <div className="h-px bg-gold/50" />
      <div className="container-x py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <Logo on="dark" />
            <p className="brand-category-line mt-3 text-[10px]" data-testid="brand-category-line">Gold ◆ Silver ◆ Diamond</p>
            <p className="text-sm text-ivory/60 mt-4 leading-relaxed max-w-xs">
              40 years of craftsmanship. Live-rate pricing, full breakup on gold, certified coins from 25 mg.
            </p>
            {(legal.legal_name || legal.cin || legal.bis_reg) && (
              <div className="mt-5 text-[11px] font-mono text-ivory/45 space-y-1" data-testid="footer-legal">
                {legal.legal_name && <p>{legal.legal_name}</p>}
                {legal.cin && <p>CIN: {legal.cin}</p>}
                {legal.bis_reg && <p>BIS: {legal.bis_reg}</p>}
                {legal.address && <p>{legal.address}</p>}
                {legal.grievance_officer && <p>Grievance: {legal.grievance_officer}</p>}
              </div>
            )}
          </div>

          <div>
            <p className="eyebrow !text-gold mb-4">SHOP</p>
            <ul className="space-y-2.5 text-sm">
              {[["Silver Jewellery", "/silver"], ["Gold Jewellery", "/gold"], ["999 Coins", "/coins"],
                ["Shop Coins by Deity", "/shop-by/deity"], ["Shop Coins by Occasion", "/shop-by/occasion"], ["Coin Selector", "/coin-selector"],
                ["Personalised", "/shop?personalised=true"], ["Shop by Bond", "/gifts"], ["The Pia Edit", "/pia"],
                ["Gift Cards", "/gift-cards"], ["Build Your Own", "/build-your-own"], ["Name Jewellery", "/name-jewellery"]].map(([label, to]) => (
                <li key={label}><Link to={to} className="hover:text-gold transition-colors" data-testid={`footer-shop-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow !text-gold mb-4">GOLD RATE BY CITY</p>
            <ul className="space-y-2.5 text-sm">
              {cities.map((c) => (
                <li key={c.slug}>
                  <Link to={`/gold-rate/${c.state.toLowerCase().replace(/\s+/g, "-")}/${c.slug}-gold-rate-today`}
                    className="hover:text-gold transition-colors" data-testid={`footer-city-${c.slug}`}>
                    Gold rate in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="eyebrow !text-gold mb-4">HELP</p>
            <ul className="space-y-2.5 text-sm">
              {POLICY_LINKS.map(([label, to]) => (
                <li key={label}><Link to={to} className="hover:text-gold transition-colors" data-testid={`footer-help-${label.toLowerCase().replace(/[\s&]+/g, "-")}`}>{label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow !text-gold mb-4">CONTACT</p>
            <ul className="space-y-2.5 text-sm" data-testid="footer-contact">
              {config?.support?.contact_person && <li className="text-ivory/70" data-testid="footer-contact-person">{config.support.contact_person}</li>}
              {config?.support?.phone && <li><a href={`tel:${config.support.phone}`} className="hover:text-gold transition-colors" data-testid="footer-phone">{config.support.phone}</a></li>}
              {config?.support?.whatsapp && (
                <li><a href={`https://wa.me/${String(config.support.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="footer-whatsapp">WhatsApp us</a></li>
              )}
              {config?.support?.hours && <li className="text-ivory/50 text-xs" data-testid="footer-hours">{config.support.hours}</li>}
            </ul>
            <p className="eyebrow !text-gold mt-8 mb-3">WE ACCEPT</p>
            <div className="flex flex-wrap gap-1.5" data-testid="payment-icons">
              {["UPI", "Visa", "Mastercard", "RuPay", "NetBanking", "EMI"].map((m) => (
                <span key={m} className="text-[10px] font-mono border border-ivory/25 rounded px-1.5 py-0.5 text-ivory/60">{m}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Popular searches — internal-linking SEO block */}
        <div className="mt-12 pt-8 border-t border-ivory/15" data-testid="popular-searches">
          <p className="eyebrow !text-gold mb-3">POPULAR SEARCHES</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {(config?.popular_searches?.length ? config.popular_searches.map((s) => (typeof s === "string" ? { label: s, url: `/search?q=${encodeURIComponent(s)}` } : s)) : POPULAR_SEARCHES.map((t) => ({ label: t, url: `/search?q=${encodeURIComponent(t)}` }))).map((t) => (
              <Link key={t.label} to={t.url} className="text-xs text-ivory/55 hover:text-gold transition-colors" data-testid={`popular-search-${t.label.replace(/\s+/g, "-")}`}>
                {t.label}
              </Link>
            ))}
          </div>
        </div>

        {partners.length > 0 && (
          <div className="mt-10 pt-8 border-t border-ivory/15" data-testid="channel-partners">
            <p className="eyebrow !text-gold mb-3">ALSO AVAILABLE ON</p>
            <div className="flex flex-wrap gap-4">
              {partners.map((p, i) => (
                p.logo ? <img key={i} src={p.logo} alt={p.name} className="h-7 object-contain opacity-80" /> :
                <span key={i} className="text-sm font-medium text-ivory/70">{p.name}</span>
              ))}
            </div>
          </div>
        )}

        {config?.app_banner_enabled && (
          <div className="mt-10 border border-gold/30 rounded-xl p-6 text-center" data-testid="app-banner">
            <p className="font-heading text-xl">The Siyaara app</p>
            <p className="text-sm text-ivory/60 mt-1">Coming soon to the App Store and Google Play.</p>
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-ivory/15 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono text-ivory/45">
          <p>© {new Date().getFullYear()} {(config?.store_name || "Siyaara").toUpperCase()} · JEWELS OF LOVE{config?.gst_number ? ` · GSTIN ${config.gst_number}` : ""}</p>
          {rate && rateLocations.includes("footer") && (
            <p data-testid="rate-strip-footer">
              Gold 999 · ₹{Number(rate.gold_24k).toLocaleString("en-IN")}/g · Silver 999 · ₹{Number(rate.silver).toLocaleString("en-IN")}/g
              <span className="text-ivory/30"> · updated {new Date(rate.updated_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>
            </p>
          )}
          <p>Prices include GST · Rates update with the market</p>
        </div>
      </div>
    </footer>
  );
}
